import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { checkIsAdmin } from "@/lib/admin-check";
import { Shield, Users, TrendingUp, FileText, MoreVertical, Ticket, UserPlus, Users2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import {
  adminGrantComp,
  adminRevokeComp,
  adminCancelSubscription,
  adminDeleteUser,
  adminInviteUser,
  adminBulkComp,
  adminCreateCoupon,
  adminRevokeCoupon,
  adminListLifetimeInvites,
  adminRemoveLifetimeInvite,
  adminResendLifetimeInvite,
  adminResendMemberLogin,
  type LifetimeInviteRow,
} from "@/lib/payments.functions";

import { adminResetDemoAccount, DEMO_EMAIL, DEMO_PASSWORD } from "@/lib/demo.functions";
import { getStripeEnvironment, hasPaymentsConfigured } from "@/lib/stripe";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [
    { title: "Admin — Wealthy Creatives Boardroom" },
    { name: "description", content: "Manage Boardroom members, account recovery, and membership access." },
    { property: "og:title", content: "Admin — Wealthy Creatives Boardroom" },
    { property: "og:description", content: "Boardroom member administration and account recovery." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex" },
  ] }),
  beforeLoad: async () => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw redirect({ to: "/auth" });
    const isAdmin = await checkIsAdmin(userData.user.id);
    // Only bounce when the check definitively says "not an admin"; a transient
    // permission/token error returns null and should not lock admins out.
    if (isAdmin === false) throw redirect({ to: "/dashboard" });
  },
  component: AdminPage,
});

type UserRow = {
  id: string;
  email: string | null;
  full_name: string | null;
  created_at: string;
  onboarding_completed_at: string | null;
  is_admin: boolean;
  access_status: string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
};


type MemberActivityRow = {
  user_id: string;
  full_name: string | null;
  email: string | null;
  access_status: string;
  nonneg_total: number;
  nonneg_done_7d: number;
  nonneg_done_30d: number;
  revenue_month_cents: number;
  mrr_cents: number;
  open_proposals: number;
  last_activity_at: string | null;
};

type CouponRow = {
  id: string;
  code: string;
  discount_percent: number;
  duration: string;
  duration_in_months: number | null;
  max_redemptions: number | null;
  redeemed_count: number;
  expires_at: string | null;
  active: boolean;
  created_at: string;
};

function AdminPage() {
  const [tab, setTab] = useState<"members" | "coupons">("members");
  const [users, setUsers] = useState<UserRow[]>([]);
  const [coupons, setCoupons] = useState<CouponRow[]>([]);
  const [invites, setInvites] = useState<LifetimeInviteRow[]>([]);
  const [activity, setActivity] = useState<MemberActivityRow[]>([]);
  const [counts, setCounts] = useState({ users: 0, proposals: 0, wins: 0 });
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const paymentsReady = hasPaymentsConfigured();
  const sendMemberReset = useServerFn(adminResendMemberLogin);
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [profilesRes, rolesRes, subsRes, propCount, winCount, couponsRes] =
        await Promise.all([
          supabase
            .from("profiles")
            .select(
              "id, email, full_name, created_at, onboarding_completed_at, access_status, trial_ends_at, current_period_end",
            )
            .order("created_at", { ascending: false }),
          supabase.from("user_roles").select("user_id, role").eq("role", "admin"),
          supabase.from("subscriptions").select("user_id, cancel_at_period_end, status"),
          supabase.from("proposals").select("*", { count: "exact", head: true }),
          supabase.from("wins").select("*", { count: "exact", head: true }),
          supabase.from("coupons").select("*").order("created_at", { ascending: false }),
        ]);

      const adminIds = new Set((rolesRes.data ?? []).map((r) => r.user_id));
      const subMap = new Map(
        (subsRes.data ?? []).map((s) => [s.user_id, s.cancel_at_period_end]),
      );
      const rows: UserRow[] = (profilesRes.data ?? []).map((p: any) => ({
        id: p.id,
        email: p.email,
        full_name: p.full_name,
        created_at: p.created_at,
        onboarding_completed_at: p.onboarding_completed_at,
        is_admin: adminIds.has(p.id),
        access_status: p.access_status ?? "trialing",
        trial_ends_at: p.trial_ends_at,
        current_period_end: p.current_period_end,
        cancel_at_period_end: !!subMap.get(p.id),
      }));

      setUsers(rows);
      setCoupons((couponsRes.data ?? []) as CouponRow[]);

      const { data: me } = await supabase.auth.getUser();
      if (me.user) {
        const { data: ov } = await (supabase.rpc as any)("admin_member_overview", {
          _admin_id: me.user.id,
        });
        setActivity((ov ?? []) as MemberActivityRow[]);
      }
      setCounts({
        users: rows.length,
        proposals: propCount.count ?? 0,
        wins: winCount.count ?? 0,
      });

      try {
        const invRes = await adminListLifetimeInvites();
        setInvites(invRes.invites);
      } catch {
        /* silent */
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleGrant(u: UserRow) {
    try {
      await adminGrantComp({ data: { targetUserId: u.id } });
      toast.success(`Comped ${u.email}`);
      load();
    } catch (e: any) {
      toast.error(e.message);
    }
  }
  async function handleRevoke(u: UserRow) {
    if (!confirm(`Revoke comp access for ${u.email}?`)) return;
    try {
      await adminRevokeComp({ data: { targetUserId: u.id } });
      toast.success("Comp revoked");
      load();
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function handleCancel(u: UserRow) {
    if (!paymentsReady) return toast.error("Payments not configured");
    if (!confirm(`Cancel ${u.email}'s subscription at period end?`)) return;
    try {
      await adminCancelSubscription({
        data: { targetUserId: u.id, environment: getStripeEnvironment() },
      });
      toast.success("Subscription will cancel at period end");
      load();
    } catch (e: any) {
      toast.error(e.message);
    }
  }
  async function handleDelete(u: UserRow) {
    const ok = prompt(`Type DELETE to hard-delete ${u.email} and all their data.`);
    if (ok !== "DELETE") return;
    try {
      await adminDeleteUser({ data: { targetUserId: u.id } });
      toast.success("User deleted");
      load();
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function handleResendLogin(u: UserRow) {
    if (!u.email) return toast.error("This member has no email on file.");
    if (resettingUserId) return;
    setResettingUserId(u.id);
    try {
      const res = await sendMemberReset({ data: { targetUserId: u.id } });
      if (res.ok) toast.success(`Password-reset email sent to ${u.email}. They can choose a new password from the link.`);
      else toast.error(res.error);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setResettingUserId(null);
    }
  }

  return (
    <div className="space-y-8">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-[color:var(--gold)]" />
          <div>
            <h1 className="font-display text-2xl font-semibold">Admin</h1>
            <p className="text-sm text-muted-foreground">Master view of the Boardroom.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-[color:var(--hairline)] hover:bg-[color:var(--surface-2)]"
          >
            <Users2 className="h-4 w-4" /> Bulk comp
          </button>
          <button
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-[color:var(--gold)] text-black hover:opacity-90"
          >
            <UserPlus className="h-4 w-4" /> Invite user
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Total members" value={counts.users} />
        <StatCard icon={FileText} label="Proposals (all users)" value={counts.proposals} />
        <StatCard icon={TrendingUp} label="Wins logged (all users)" value={counts.wins} />
      </div>

      <div className="flex gap-2 border-b border-[color:var(--hairline)]">
        {(["members", "coupons"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm capitalize border-b-2 -mb-px transition ${
              tab === t
                ? "border-[color:var(--gold)] text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t}
          </button>
        ))}

      </div>

      {tab === "members" && (
        <div className="space-y-8">
          <MembersTable
            loading={loading}
            users={users}
            onGrant={handleGrant}
            onRevoke={handleRevoke}
            onCancel={handleCancel}
            onDelete={handleDelete}
            onResendLogin={handleResendLogin}
            resettingUserId={resettingUserId}
          />

          <MemberActivityPanel loading={loading} rows={activity} />

          <LifetimeInvitesPanel loading={loading} invites={invites} reload={load} />

          <DemoAccountPanel />
        </div>
      )}
      {tab === "coupons" && (
        <CouponsPanel
          loading={loading}
          coupons={coupons}
          onNew={() => setCouponOpen(true)}
          reload={load}
          paymentsReady={paymentsReady}
        />
      )}

      {inviteOpen && (
        <InviteModal
          onClose={() => setInviteOpen(false)}
          onDone={() => {
            setInviteOpen(false);
            load();
          }}
        />
      )}
      {bulkOpen && (
        <BulkCompModal
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            setBulkOpen(false);
            load();
          }}
        />
      )}
      {couponOpen && (
        <CouponModal
          onClose={() => setCouponOpen(false)}
          onDone={() => {
            setCouponOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function MembersTable({
  loading,
  users,
  onGrant,
  onRevoke,
  onCancel,
  onDelete,
  onResendLogin,
  resettingUserId,
}: {
  loading: boolean;
  users: UserRow[];
  onGrant: (u: UserRow) => void;
  onRevoke: (u: UserRow) => void;
  onCancel: (u: UserRow) => void;
  onDelete: (u: UserRow) => void;
  onResendLogin: (u: UserRow) => void;
  resettingUserId: string | null;
}) {
  const [openMenu, setOpenMenu] = useState<string | null>(null);


  return (
    <div className="boardroom-card overflow-hidden">
      {loading ? (
        <div className="px-6 py-10 text-sm text-muted-foreground">Loading…</div>
      ) : users.length === 0 ? (
        <div className="px-6 py-10 text-sm text-muted-foreground">No members yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-[color:var(--surface-2)]/40">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Member</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Joined</th>
                <th className="text-left px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3">Account</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-[color:var(--hairline)]">
                  <td className="px-6 py-3">
                    <div className="font-medium">{u.full_name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">{u.email ?? "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge u={u} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    {u.is_admin ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-[color:var(--gold)]/10 text-[color:var(--gold)]">
                        <Shield className="h-3 w-3" /> Admin
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Member</span>
                    )}
                  </td>
                  <td className="px-4 py-3 relative text-right">
                    <Button type="button" variant="outline" size="sm" className="mr-2" disabled={!u.email || !!resettingUserId} onClick={() => onResendLogin(u)}>
                      <KeyRound /> {resettingUserId === u.id ? "Sending…" : "Reset password"}
                    </Button>
                    <button
                      onClick={() => setOpenMenu(openMenu === u.id ? null : u.id)}
                      className="p-1 hover:bg-[color:var(--surface-2)] rounded"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {openMenu === u.id && (
                      <div
                        className="absolute right-4 mt-1 w-56 boardroom-card z-50 py-1 text-left"
                        onMouseLeave={() => setOpenMenu(null)}
                      >
                        <MenuItem onClick={() => { setOpenMenu(null); onResendLogin(u); }}>
                          Send password-reset email
                        </MenuItem>
                        {u.access_status !== "comped" ? (
                          <MenuItem onClick={() => { setOpenMenu(null); onGrant(u); }}>
                            Grant comp access
                          </MenuItem>
                        ) : (
                          <MenuItem onClick={() => { setOpenMenu(null); onRevoke(u); }}>
                            Revoke comp access
                          </MenuItem>
                        )}
                        <MenuItem onClick={() => { setOpenMenu(null); onCancel(u); }}>
                          Cancel subscription
                        </MenuItem>
                        <div className="border-t border-[color:var(--hairline)] my-1" />
                        <MenuItem
                          onClick={() => { setOpenMenu(null); onDelete(u); }}
                          danger
                        >
                          Delete user…
                        </MenuItem>

                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ u }: { u: UserRow }) {
  const s = u.access_status;
  const map: Record<string, { label: string; cls: string }> = {
    trialing: { label: "Trialing", cls: "text-[color:var(--teal)]" },
    active: { label: "Active", cls: "text-[color:var(--gold)]" },
    comped: { label: "Comped", cls: "text-[color:var(--gold)]" },
    past_due: { label: "Past due", cls: "text-orange-400" },
    canceled: { label: "Canceled", cls: "text-muted-foreground" },
    trial_expired: { label: "Trial ended", cls: "text-muted-foreground" },
  };
  const info = map[s] ?? { label: s, cls: "text-muted-foreground" };
  let hint = "";
  if (s === "trialing" && u.trial_ends_at) {
    const days = Math.ceil(
      (new Date(u.trial_ends_at).getTime() - Date.now()) / 86400000,
    );
    hint = `${Math.max(0, days)}d left`;
  }
  if (u.cancel_at_period_end && u.current_period_end) {
    hint = `Ends ${new Date(u.current_period_end).toLocaleDateString()}`;
  }
  return (
    <div className="flex flex-col">
      <span className={`text-xs font-medium ${info.cls}`}>{info.label}</span>
      {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--surface-2)] ${
        danger ? "text-red-400" : "text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function CouponsPanel({
  loading,
  coupons,
  onNew,
  reload,
  paymentsReady,
}: {
  loading: boolean;
  coupons: CouponRow[];
  onNew: () => void;
  reload: () => void;
  paymentsReady: boolean;
}) {
  async function revoke(c: CouponRow) {
    if (!confirm(`Deactivate code ${c.code}?`)) return;
    try {
      await adminRevokeCoupon({
        data: { couponId: c.id, environment: getStripeEnvironment() },
      });
      toast.success("Code deactivated");
      reload();
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Codes redeem at checkout. 100%-off codes can also be redeemed directly on the paywall.
        </div>
        <button
          onClick={onNew}
          disabled={!paymentsReady}
          className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-[color:var(--gold)] text-black hover:opacity-90 disabled:opacity-40"
        >
          <Ticket className="h-4 w-4" /> New code
        </button>
      </div>
      <div className="boardroom-card overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-sm text-muted-foreground">Loading…</div>
        ) : coupons.length === 0 ? (
          <div className="px-6 py-10 text-sm text-muted-foreground">
            No coupon codes yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-[color:var(--surface-2)]/40">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Code</th>
                <th className="text-left px-4 py-3 font-medium">Discount</th>
                <th className="text-left px-4 py-3 font-medium">Duration</th>
                <th className="text-left px-4 py-3 font-medium">Redeemed</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.id} className="border-t border-[color:var(--hairline)]">
                  <td className="px-6 py-3 font-mono text-xs">{c.code}</td>
                  <td className="px-4 py-3">{c.discount_percent}% off</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {c.duration}
                    {c.duration === "repeating" && c.duration_in_months
                      ? ` (${c.duration_in_months}mo)`
                      : ""}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {c.redeemed_count}
                    {c.max_redemptions ? ` / ${c.max_redemptions}` : ""}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {c.active ? (
                      <span className="text-[color:var(--teal)]">Active</span>
                    ) : (
                      <span className="text-muted-foreground">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.active && (
                      <button
                        onClick={() => revoke(c)}
                        className="text-xs text-red-400 hover:underline"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function InviteModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [comp, setComp] = useState(true);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!email.trim()) return toast.error("Email required");
    setBusy(true);
    try {
      const res = await adminInviteUser({
        data: {
          email: email.trim(),
          fullName: fullName.trim(),
          comp,
        },
      });
      if (!res.ok) {
        toast.error(res.error);
        return;
      }
      if (res.alreadyExisted) {
        toast.success(comp ? "Existing user comped." : "User already had an account.");
      } else if (res.preapproved) {
        toast.success("Lifetime access saved. They can sign in or create an account with this email to claim it.");
      } else {
        toast.success(comp ? "Invitation sent · comped for life." : "Invitation sent.");
      }
      onDone();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Invite user" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Email</label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            className="w-full bg-[color:var(--surface-2)] border border-[color:var(--hairline)] rounded-md px-3 py-2 text-sm mt-1"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">
            Full name (optional)
          </label>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full bg-[color:var(--surface-2)] border border-[color:var(--hairline)] rounded-md px-3 py-2 text-sm mt-1"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={comp} onChange={(e) => setComp(e.target.checked)} />
          Grant lifetime comp access (recommended for your clients)
        </label>
        {comp && (
          <p className="text-xs text-muted-foreground">
            If the email invite cannot be sent yet, their lifetime access will still be saved for this email.
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm text-muted-foreground px-3 py-2">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="bg-[color:var(--gold)] text-black text-sm px-4 py-2 rounded-md disabled:opacity-40"
          >
            {busy ? "Working…" : comp ? "Grant lifetime access" : "Send invite"}
          </button>
        </div>
      </div>
    </Modal>
  );
}


function BulkCompModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const emails = Array.from(
    new Set(
      raw
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)),
    ),
  );

  async function submit() {
    if (emails.length === 0) return toast.error("Paste at least one valid email.");
    setBusy(true);
    try {
      const res = await adminBulkComp({
        data: {
          emails,
        },
      });
      const parts: string[] = [];
      if (res.invited.length) parts.push(`${res.invited.length} invited or pre-approved`);
      if (res.comped.length) parts.push(`${res.comped.length} comped`);
      if (res.failed.length) parts.push(`${res.failed.length} failed`);
      toast.success(parts.join(" · ") || "Done");
      if (res.failed.length) {
        console.warn("Bulk comp failures:", res.failed);
        toast.error(
          `Failed: ${res.failed.slice(0, 3).map((f) => f.email).join(", ")}${res.failed.length > 3 ? "…" : ""}`,
        );
      }
      onDone();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Bulk comp cohort" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Paste emails separated by commas, spaces, or new lines. Existing users get lifetime access; new emails are invited and comped on sign-up.
        </p>
        <textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={8}
          placeholder={"jane@studio.com\njohn@lens.co\nmaria@design.io"}
          className="w-full bg-[color:var(--surface-2)] border border-[color:var(--hairline)] rounded-md px-3 py-2 text-sm font-mono"
        />
        <div className="text-xs text-muted-foreground">
          {emails.length} valid email{emails.length === 1 ? "" : "s"} detected
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm text-muted-foreground px-3 py-2">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || emails.length === 0}
            className="bg-[color:var(--gold)] text-black text-sm px-4 py-2 rounded-md disabled:opacity-40"
          >
            {busy ? "Working…" : `Comp ${emails.length || ""}`.trim()}
          </button>
        </div>
      </div>
    </Modal>
  );
}


function CouponModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(100);
  const [duration, setDuration] = useState<"once" | "repeating" | "forever">("forever");
  const [durationInMonths, setDurationInMonths] = useState(3);
  const [maxRedemptions, setMaxRedemptions] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!code.trim()) return toast.error("Code required");
    setBusy(true);
    try {
      const res = await adminCreateCoupon({
        data: {
          code,
          discountPercent,
          duration,
          durationInMonths: duration === "repeating" ? durationInMonths : undefined,
          maxRedemptions: maxRedemptions ? Number(maxRedemptions) : undefined,
          expiresAt: expiresAt || undefined,
          environment: getStripeEnvironment(),
        },
      });
      if ("error" in res) {
        toast.error(res.error);
      } else {
        toast.success(`Code ${code.toUpperCase()} created`);
        onDone();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="New coupon code" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Code</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="WC-JANE-2026"
            className="w-full bg-[color:var(--surface-2)] border border-[color:var(--hairline)] rounded-md px-3 py-2 text-sm font-mono mt-1"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              % off
            </label>
            <input
              type="number"
              min={1}
              max={100}
              value={discountPercent}
              onChange={(e) => setDiscountPercent(Number(e.target.value))}
              className="w-full bg-[color:var(--surface-2)] border border-[color:var(--hairline)] rounded-md px-3 py-2 text-sm mt-1"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Duration
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value as any)}
              className="w-full bg-[color:var(--surface-2)] border border-[color:var(--hairline)] rounded-md px-3 py-2 text-sm mt-1"
            >
              <option value="forever">Forever</option>
              <option value="once">Once</option>
              <option value="repeating">Repeating</option>
            </select>
          </div>
        </div>
        {duration === "repeating" && (
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Months
            </label>
            <input
              type="number"
              min={1}
              value={durationInMonths}
              onChange={(e) => setDurationInMonths(Number(e.target.value))}
              className="w-full bg-[color:var(--surface-2)] border border-[color:var(--hairline)] rounded-md px-3 py-2 text-sm mt-1"
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Max redemptions
            </label>
            <input
              type="number"
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
              placeholder="Unlimited"
              className="w-full bg-[color:var(--surface-2)] border border-[color:var(--hairline)] rounded-md px-3 py-2 text-sm mt-1"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Expires
            </label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full bg-[color:var(--surface-2)] border border-[color:var(--hairline)] rounded-md px-3 py-2 text-sm mt-1"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm text-muted-foreground px-3 py-2">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="bg-[color:var(--gold)] text-black text-sm px-4 py-2 rounded-md disabled:opacity-40"
          >
            {busy ? "Creating…" : "Create code"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md boardroom-card p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Users;
  label: string;
  value: number;
}) {
  return (
    <div className="boardroom-card p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-3">
        <Icon className="h-4 w-4 text-[color:var(--teal)]" />
        {label}
      </div>
      <div className="font-display text-3xl font-semibold">{value}</div>
    </div>
  );
}

function formatRelative(iso: string | null) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const abs = Math.abs(diff);
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  if (abs < min) return "just now";
  if (abs < hr) return `${Math.round(abs / min)}m ago`;
  if (abs < day) return `${Math.round(abs / hr)}h ago`;
  return `${Math.round(abs / day)}d ago`;
}

function DeliveryBadge({ status }: { status: LifetimeInviteRow["delivery_status"] }) {
  const map: Record<string, { label: string; cls: string; title: string }> = {
    emailed: {
      label: "Emailed",
      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
      title: "Invite email was sent successfully.",
    },
    preapproved: {
      label: "Pre-approved",
      cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",
      title: "Email couldn't be sent. They can still claim lifetime access by signing up with this email.",
    },
    already_registered: {
      label: "Already registered",
      cls: "bg-sky-500/15 text-sky-300 border-sky-500/30",
      title: "Account already existed — comped in place.",
    },
  };
  const meta = map[status] ?? map.preapproved;
  return (
    <span
      title={meta.title}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
}

function fmtMoney(cents: number) {
  return (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function fmtAgo(iso: string | null) {
  if (!iso || iso.startsWith("-infinity")) return "—";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function MemberActivityPanel({
  loading,
  rows,
}: {
  loading: boolean;
  rows: MemberActivityRow[];
}) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">Member activity</h2>
        <p className="text-sm text-muted-foreground">
          Aggregate stats only — no journal or entry text. Use it to keep members accountable.
        </p>
      </div>
      <div className="overflow-x-auto rounded-lg border border-[color:var(--hairline)]">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-card/60 text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Member</th>
              <th className="text-left px-4 py-3 font-medium">Non-negotiables (7d)</th>
              <th className="text-left px-4 py-3 font-medium">Non-negotiables (30d)</th>
              <th className="text-left px-4 py-3 font-medium">Revenue (this month)</th>
              <th className="text-left px-4 py-3 font-medium">MRR</th>
              <th className="text-left px-4 py-3 font-medium">Open deals</th>
              <th className="text-left px-4 py-3 font-medium">Last active</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                  No member activity yet.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const stale =
                !r.last_activity_at ||
                r.last_activity_at.startsWith("-infinity") ||
                Date.now() - new Date(r.last_activity_at).getTime() > 7 * 86400000;
              return (
                <tr key={r.user_id} className="border-t border-[color:var(--hairline)]">
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.full_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{r.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {r.nonneg_done_7d}
                    <span className="text-xs text-muted-foreground"> / {r.nonneg_total * 7} possible</span>
                  </td>
                  <td className="px-4 py-3">{r.nonneg_done_30d}</td>
                  <td className="px-4 py-3">{fmtMoney(r.revenue_month_cents)}</td>
                  <td className="px-4 py-3">{fmtMoney(r.mrr_cents)}</td>
                  <td className="px-4 py-3">{r.open_proposals}</td>
                  <td className={`px-4 py-3 ${stale ? "text-[color:var(--gold)]" : ""}`}>
                    {fmtAgo(r.last_activity_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LifetimeInvitesPanel({
  loading,
  invites,
  reload,
}: {
  loading: boolean;
  invites: LifetimeInviteRow[];
  reload: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  async function handleResend(inv: LifetimeInviteRow) {
    setBusy(inv.id);
    try {
      const res = await adminResendLifetimeInvite({ data: { inviteId: inv.id } });
      if (res.ok) toast.success(`Invite re-sent to ${inv.email}`);
      else toast.error(res.error ?? "Could not resend invite");
      reload();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  }

  async function handleRemove(inv: LifetimeInviteRow) {
    if (!confirm(`Remove the lifetime invite for ${inv.email}?`)) return;
    const revokeAccess = confirm(
      "Also revoke their lifetime access?\n\nOK = revoke access too\nCancel = only remove the log entry",
    );
    setBusy(inv.id);
    try {
      const res = await adminRemoveLifetimeInvite({
        data: { inviteId: inv.id, revokeAccess },
      });
      toast.success(res.revoked ? "Invite removed and access revoked" : "Invite removed");
      reload();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="boardroom-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Lifetime access log</h2>
          <p className="text-xs text-muted-foreground">
            Delivery status for every lifetime invite. Pre-approved means the email couldn't be sent — they can still claim access by signing up with that email.
          </p>
        </div>
        <div className="text-xs text-muted-foreground">{invites.length} total</div>
      </div>
      {loading && invites.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
      ) : invites.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">
          No lifetime invites yet. Use "Invite user" or "Bulk comp" above.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-5 md:mx-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-[color:var(--hairline)]">
                <th className="py-2 px-3">Email</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Last attempt</th>
                <th className="py-2 px-3">Claimed</th>
                <th className="py-2 px-3">Reason (if failed)</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.id} className="border-b border-[color:var(--hairline)]/50">
                  <td className="py-2 px-3">
                    <div className="font-medium">{inv.email}</div>
                    {inv.full_name && (
                      <div className="text-xs text-muted-foreground">{inv.full_name}</div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <DeliveryBadge status={inv.delivery_status} />
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">
                    <span title={inv.delivery_attempted_at ?? inv.updated_at}>
                      {formatRelative(inv.delivery_attempted_at ?? inv.updated_at)}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    {inv.claimed_at ? (
                      <span
                        title={inv.claimed_at}
                        className="inline-flex items-center rounded-full border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/10 px-2 py-0.5 text-[11px] font-medium text-[color:var(--gold)]"
                      >
                        ★ Claimed · {formatRelative(inv.claimed_at)}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Not yet</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs text-muted-foreground max-w-[280px] truncate">
                    {inv.delivery_error ? (
                      <span title={inv.delivery_error}>{inv.delivery_error}</span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        disabled={busy === inv.id}
                        onClick={() => handleResend(inv)}
                        className="text-xs px-2 py-1 rounded-md border border-[color:var(--hairline)] hover:bg-[color:var(--surface-2)] disabled:opacity-50"
                      >
                        Resend
                      </button>
                      <button
                        disabled={busy === inv.id}
                        onClick={() => handleRemove(inv)}
                        className="text-xs px-2 py-1 rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * A shared demo login, pre-filled with realistic fake data, so the Boardroom
 * can be shown to a prospect without exposing any real member's numbers.
 */
function DemoAccountPanel() {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [creds, setCreds] = useState<{ email: string; password: string } | null>(null);

  // No window.confirm(): it is silently blocked inside embedded previews,
  // which made the button look like it did nothing.
  async function handleReset() {
    setConfirming(false);
    setBusy(true);
    try {
      const res = await adminResetDemoAccount();
      if ((res as any)?.error) {
        toast.error((res as any).error);
      } else {
        setCreds({ email: (res as any).email, password: (res as any).password });
        toast.success("Demo account is ready.");
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="boardroom-card p-5">
      <h2 className="text-[11px] font-semibold uppercase tracking-widest text-foreground">Demo account</h2>
      <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
        One shared login filled with realistic sample numbers — revenue, deals, leads, content, tasks and wins — so you can
        walk someone through the Boardroom without showing your own data. Reset it any time to wipe changes a guest made.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {confirming ? (
          <>
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-[color:var(--gold)] text-black hover:opacity-90"
            >
              Yes, replace demo data
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="text-sm px-3 py-2 rounded-md border border-[color:var(--hairline)] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            disabled={busy}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-[color:var(--gold)] text-black hover:opacity-90 disabled:opacity-60"
          >
            {busy ? "Working…" : "Create / reset demo account"}
          </button>
        )}
        <span className="text-xs text-muted-foreground">
          Login: <span className="text-foreground">{DEMO_EMAIL}</span> · Password:{" "}
          <span className="text-foreground">{DEMO_PASSWORD}</span>
        </span>
      </div>

      {creds && (
        <p className="mt-3 text-xs text-[color:var(--teal)]">
          Ready. Sign in at the normal login with {creds.email} / {creds.password}.
        </p>
      )}
    </div>
  );
}
